import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save, 
  QrCode, 
  Download,
  Move,
  Square,
  Circle,
  Umbrella,
  Tent,
  LayoutGrid,
  Settings,
  DoorOpen,
  Bath,
  Minus
} from 'lucide-react';
import axios from 'axios';
import ModernFloorEditor from '@/modern/floor/ModernFloorEditor';
import { useIsModern } from '@/modern/shared';
import { toast } from 'sonner';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

function LayoutEditor() {
  const navigate = useNavigate();
  const { spaceId } = useParams();
  const { user } = useAuth();
  
  const [spaces, setSpaces] = useState([]);
  const [currentSpace, setCurrentSpace] = useState(null);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showSpaceDialog, setShowSpaceDialog] = useState(false);
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrData, setQrData] = useState(null);
  
  // Form states
  const [spaceForm, setSpaceForm] = useState({
    name: '',
    space_type: 'indoor',
    width: 800,
    height: 600
  });
  
  const [unitForm, setUnitForm] = useState({
    name: '',
    unit_type: 'table',
    capacity: 4,
    shape: 'rectangle',
    consumption_mode: 'shared',
    width: 60,
    height: 60
  });

  useEffect(() => {
    fetchSpaces();
  }, []);

  useEffect(() => {
    if (spaceId && spaces.length > 0) {
      const space = spaces.find(s => s.space_id === spaceId);
      if (space) {
        setCurrentSpace(space);
        fetchUnits(spaceId);
      }
    }
  }, [spaceId, spaces]);

  const fetchSpaces = async () => {
    try {
      const response = await axios.get(`${API}/spaces`, { withCredentials: true });
      setSpaces(response.data);
      
      // If no spaceId in URL but spaces exist, select the first one
      if (!spaceId && response.data.length > 0) {
        navigate(`/layout-editor/${response.data[0].space_id}`, { replace: true });
      }
    } catch (error) {
      console.error('Error fetching spaces:', error);
      toast.error('Error al cargar espacios');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async (sid) => {
    try {
      const response = await axios.get(`${API}/spaces/${sid}/units`, { withCredentials: true });
      setUnits(response.data);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const handleCreateSpace = async () => {
    try {
      const response = await axios.post(`${API}/spaces`, spaceForm, { withCredentials: true });
      setSpaces([...spaces, response.data]);
      setShowSpaceDialog(false);
      setSpaceForm({ name: '', space_type: 'indoor', width: 800, height: 600 });
      toast.success('Espacio creado exitosamente');
      navigate(`/layout-editor/${response.data.space_id}`);
    } catch (error) {
      toast.error('Error al crear espacio');
    }
  };

  const handleCreateUnit = async () => {
    if (!currentSpace) return;
    
    try {
      const newUnit = {
        ...unitForm,
        x: 100 + Math.random() * 200,
        y: 100 + Math.random() * 200
      };
      
      const response = await axios.post(
        `${API}/spaces/${currentSpace.space_id}/units`, 
        newUnit, 
        { withCredentials: true }
      );
      
      setUnits([...units, response.data]);
      setShowUnitDialog(false);
      setUnitForm({
        name: '',
        unit_type: 'table',
        capacity: 4,
        shape: 'rectangle',
        consumption_mode: 'shared',
        width: 60,
        height: 60
      });
      toast.success('Unidad creada exitosamente');
    } catch (error) {
      toast.error('Error al crear unidad');
    }
  };

  const handleDeleteUnit = async (unitId) => {
    try {
      await axios.delete(`${API}/units/${unitId}`, { withCredentials: true });
      setUnits(units.filter(u => u.unit_id !== unitId));
      setSelectedUnit(null);
      toast.success('Unidad eliminada');
    } catch (error) {
      toast.error('Error al eliminar unidad');
    }
  };

  const handleDeleteSpace = async () => {
    if (!currentSpace) return;
    
    try {
      await axios.delete(`${API}/spaces/${currentSpace.space_id}`, { withCredentials: true });
      setSpaces(spaces.filter(s => s.space_id !== currentSpace.space_id));
      setCurrentSpace(null);
      setUnits([]);
      toast.success('Espacio eliminado');
      
      if (spaces.length > 1) {
        const nextSpace = spaces.find(s => s.space_id !== currentSpace.space_id);
        if (nextSpace) {
          navigate(`/layout-editor/${nextSpace.space_id}`);
        }
      } else {
        navigate('/layout-editor');
      }
    } catch (error) {
      toast.error('Error al eliminar espacio');
    }
  };

  const handleMouseDown = (e, unit) => {
    e.preventDefault();
    setSelectedUnit(unit);
    setIsDragging(true);
    
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !selectedUnit) return;
    
    const canvas = document.getElementById('layout-canvas');
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left - dragOffset.x, currentSpace.width - selectedUnit.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top - dragOffset.y, currentSpace.height - selectedUnit.height));
    
    setUnits(units.map(u => 
      u.unit_id === selectedUnit.unit_id 
        ? { ...u, x, y }
        : u
    ));
  };

  const handleMouseUp = async () => {
    if (isDragging && selectedUnit) {
      const unit = units.find(u => u.unit_id === selectedUnit.unit_id);
      if (unit) {
        try {
          await axios.put(
            `${API}/units/${unit.unit_id}/position?x=${unit.x}&y=${unit.y}`,
            {},
            { withCredentials: true }
          );
        } catch (error) {
          console.error('Error updating position:', error);
        }
      }
    }
    setIsDragging(false);
  };

  const handleShowQR = async (unit) => {
    try {
      const response = await axios.get(
        `${API}/units/${unit.unit_id}/qr-data`,
        { withCredentials: true }
      );
      setQrData(response.data);
      setShowQRDialog(true);
    } catch (error) {
      toast.error('Error al generar QR');
    }
  };

  const getUnitColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 border-green-500';
      case 'occupied': return 'bg-yellow-100 border-yellow-500';
      case 'consuming': return 'bg-blue-100 border-blue-500';
      case 'pending_payment': return 'bg-red-100 border-red-500';
      default: return 'bg-gray-100 border-gray-400';
    }
  };

  const getUnitIcon = (type) => {
    switch (type) {
      case 'umbrella': return Umbrella;
      case 'tent': return Tent;
      case 'door': return DoorOpen;
      case 'bathroom': return Bath;
      case 'divider': return Minus;
      case 'bar': return Square;
      default: return Square;
    }
  };
  
  // Check if unit type is decorative (non-service)
  const isDecorativeUnit = (type) => {
    return ['door', 'bathroom', 'divider', 'bar'].includes(type);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-semibold">Editor de Espacios</h1>
            <p className="text-sm text-muted-foreground">
              {currentSpace ? currentSpace.name : 'Selecciona o crea un espacio'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Dialog open={showSpaceDialog} onOpenChange={setShowSpaceDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="new-space-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Espacio
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Nuevo Espacio</DialogTitle>
                <DialogDescription>
                  Define las características del espacio
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nombre</Label>
                  <Input
                    placeholder="Ej: Salón Principal"
                    value={spaceForm.name}
                    onChange={(e) => setSpaceForm({ ...spaceForm, name: e.target.value })}
                    data-testid="space-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Espacio</Label>
                  <Select
                    value={spaceForm.space_type}
                    onValueChange={(value) => setSpaceForm({ ...spaceForm, space_type: value })}
                  >
                    <SelectTrigger data-testid="space-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indoor">Interior</SelectItem>
                      <SelectItem value="outdoor">Exterior</SelectItem>
                      <SelectItem value="terrace">Terraza</SelectItem>
                      <SelectItem value="beach">Playa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ancho (px)</Label>
                    <Input
                      type="number"
                      value={spaceForm.width}
                      onChange={(e) => setSpaceForm({ ...spaceForm, width: parseInt(e.target.value) || 800 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Alto (px)</Label>
                    <Input
                      type="number"
                      value={spaceForm.height}
                      onChange={(e) => setSpaceForm({ ...spaceForm, height: parseInt(e.target.value) || 600 })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSpaceDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateSpace} data-testid="create-space-btn">
                  Crear Espacio
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="flex">
        {/* Spaces Sidebar */}
        <div className="w-64 border-r border-border p-4 hidden md:block h-[calc(100vh-64px)] overflow-y-auto">
          <h3 className="font-semibold mb-4">Espacios</h3>
          
          {spaces.length === 0 ? (
            <div className="text-center py-8">
              <LayoutGrid className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No hay espacios</p>
            </div>
          ) : (
            <div className="space-y-2">
              {spaces.map((space) => (
                <div
                  key={space.space_id}
                  className={`p-3 rounded-xl cursor-pointer transition-colors ${
                    currentSpace?.space_id === space.space_id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => navigate(`/layout-editor/${space.space_id}`)}
                >
                  <p className="font-medium">{space.name}</p>
                  <p className="text-xs opacity-80 capitalize">{space.space_type}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 p-4 md:p-6">
          {currentSpace ? (
            <>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-unit-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Mesa/Unidad
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Nueva Unidad</DialogTitle>
                        <DialogDescription>
                          Configura la mesa, carpa o sombrilla
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Nombre/Número</Label>
                          <Input
                            placeholder="Ej: Mesa 1"
                            value={unitForm.name}
                            onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })}
                            data-testid="unit-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Tipo</Label>
                          <Select
                            value={unitForm.unit_type}
                            onValueChange={(value) => setUnitForm({ ...unitForm, unit_type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="table">Mesa</SelectItem>
                              <SelectItem value="umbrella">Sombrilla</SelectItem>
                              <SelectItem value="tent">Carpa</SelectItem>
                              <SelectItem value="door">Puerta (decorativo)</SelectItem>
                              <SelectItem value="bathroom">Baño (decorativo)</SelectItem>
                              <SelectItem value="divider">Línea divisora (decorativo)</SelectItem>
                              <SelectItem value="bar">Barra (decorativo)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Only show capacity for service units, not decorative elements */}
                        {!['door', 'bathroom', 'divider', 'bar'].includes(unitForm.unit_type) && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Capacidad</Label>
                            <Input
                              type="number"
                              value={unitForm.capacity}
                              onChange={(e) => setUnitForm({ ...unitForm, capacity: parseInt(e.target.value) || 4 })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Forma</Label>
                            <Select
                              value={unitForm.shape}
                              onValueChange={(value) => setUnitForm({ ...unitForm, shape: value })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="rectangle">Rectángulo</SelectItem>
                                <SelectItem value="circle">Círculo</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        )}
                        {/* Only show consumption mode for service units */}
                        {!['door', 'bathroom', 'divider', 'bar'].includes(unitForm.unit_type) && (
                        <div className="space-y-2">
                          <Label>Modo de Consumo</Label>
                          <Select
                            value={unitForm.consumption_mode}
                            onValueChange={(value) => setUnitForm({ ...unitForm, consumption_mode: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="shared">Cuenta Única (compartida)</SelectItem>
                              <SelectItem value="individual">Check-in Individual</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        )}
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUnitDialog(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleCreateUnit} data-testid="create-unit-btn">
                          Crear Unidad
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Button variant="outline" onClick={handleDeleteSpace}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Espacio
                  </Button>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-100 border-2 border-green-500"></div>
                    <span>Libre</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-100 border-2 border-yellow-500"></div>
                    <span>Ocupada</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-100 border-2 border-blue-500"></div>
                    <span>Consumiendo</span>
                  </div>
                </div>
              </div>

              {/* Canvas */}
              <div 
                id="layout-canvas"
                className="layout-canvas border border-border"
                style={{ 
                  width: currentSpace.width, 
                  height: currentSpace.height,
                  maxWidth: '100%'
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {units.map((unit) => {
                  const Icon = getUnitIcon(unit.unit_type);
                  const isDecorative = isDecorativeUnit(unit.unit_type);
                  return (
                    <div
                      key={unit.unit_id}
                      className={`unit-item ${isDecorative ? 'bg-gray-300 dark:bg-gray-600 border-dashed' : getUnitColor(unit.status)} ${
                        selectedUnit?.unit_id === unit.unit_id ? 'selected' : ''
                      } ${unit.shape === 'circle' ? 'rounded-full' : 'rounded-lg'}`}
                      style={{
                        left: unit.x,
                        top: unit.y,
                        width: unit.width,
                        height: unit.unit_type === 'divider' ? 8 : unit.height,
                        transform: `rotate(${unit.rotation}deg)`,
                        opacity: isDecorative ? 0.7 : 1
                      }}
                      onMouseDown={(e) => handleMouseDown(e, unit)}
                      onClick={() => setSelectedUnit(unit)}
                    >
                      {!isDecorative && <span className="text-xs font-bold">{unit.name}</span>}
                      {isDecorative && <Icon className="w-4 h-4" />}
                    </div>
                  );
                })}
              </div>

              {/* Selected Unit Details */}
              {selectedUnit && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-lg">{selectedUnit.name}</CardTitle>
                    <CardDescription>
                      {selectedUnit.unit_type === 'table' ? 'Mesa' : 
                       selectedUnit.unit_type === 'umbrella' ? 'Sombrilla' : 
                       selectedUnit.unit_type === 'tent' ? 'Carpa' :
                       selectedUnit.unit_type === 'door' ? 'Puerta' :
                       selectedUnit.unit_type === 'bathroom' ? 'Baño' :
                       selectedUnit.unit_type === 'divider' ? 'Línea divisora' :
                       selectedUnit.unit_type === 'bar' ? 'Barra' : 'Elemento'} 
                      {!isDecorativeUnit(selectedUnit.unit_type) && (
                        <span> • Capacidad: {selectedUnit.capacity} personas</span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {!isDecorativeUnit(selectedUnit.unit_type) && (
                        <Button variant="outline" onClick={() => handleShowQR(selectedUnit)}>
                          <QrCode className="w-4 h-4 mr-2" />
                          Ver QR
                        </Button>
                      )}
                      <Button 
                        variant="destructive" 
                        onClick={() => handleDeleteUnit(selectedUnit.unit_id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-96">
              <LayoutGrid className="w-16 h-16 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">No hay espacio seleccionado</h2>
              <p className="text-muted-foreground mb-4">
                Crea un nuevo espacio para comenzar a diseñar tu layout
              </p>
              <Button onClick={() => setShowSpaceDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear Espacio
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* QR Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Código QR - {qrData?.unit_name}</DialogTitle>
            <DialogDescription>
              Imprime o descarga este código para la mesa
            </DialogDescription>
          </DialogHeader>
          {qrData && (
            <div className="flex flex-col items-center py-4">
              <img 
                src={qrData.qr_image} 
                alt="QR Code" 
                className="w-64 h-64 mb-4"
              />
              <p className="text-sm text-muted-foreground text-center break-all">
                {qrData.url}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRDialog(false)}>
              Cerrar
            </Button>
            {qrData && (
              <Button asChild>
                <a href={qrData.qr_image} download={`qr-${qrData.unit_name}.png`}>
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function LayoutEditorPage() {
  return useIsModern() ? <ModernFloorEditor /> : <LayoutEditor />;
}
